import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  icon?: LucideIcon;
  disabled?: boolean;
  title?: string;
}

/**
 * Segmentli tanlov (iOS/Tesla uslubi): to'ldirilgan yo'lak, faol bo'lak ko'tarilgan.
 * Davr, doira (filial), ko'rinish (ro'yxat/doska), rejim va til uchun -- butun ilovada bitta ko'rinish.
 */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  size = "md",
  className,
  ariaLabel,
  fullWidth,
}: {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: "sm" | "md";
  className?: string;
  ariaLabel?: string;
  fullWidth?: boolean;
}) {
  return (
    <div role="group" aria-label={ariaLabel} className={cn("inline-flex max-w-full flex-wrap gap-0.5 rounded-[12px] bg-fill p-[3px]", fullWidth && "flex w-full", className)}>
      {options.map((o) => {
        const active = o.value === value;
        const Icon = o.icon;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            disabled={o.disabled}
            title={o.title}
            onClick={() => onChange(o.value)}
            className={cn(
              "inline-flex items-center justify-center gap-1.5 rounded-[9px] whitespace-nowrap transition-[background-color,color,box-shadow] duration-150",
              size === "sm" ? "h-7 px-2.5 text-[12px]" : "h-8 px-3.5 text-[13px]",
              fullWidth && "flex-1",
              active ? "bg-raised font-semibold text-ink shadow-pill" : "font-medium text-muted hover:text-ink",
              o.disabled && "cursor-not-allowed opacity-50 hover:text-muted",
            )}
          >
            {Icon && <Icon size={size === "sm" ? 13 : 14} strokeWidth={2} aria-hidden />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
